import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Snackbar,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Tabs,
  Tab,
  Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import config from '../../../config';

const STATUS_OPTIONS = [
  { value: 'Present',   color: '#16a34a', bg: '#dcfce7' },
  { value: 'Absent',    color: '#dc2626', bg: '#fee2e2' },
  { value: 'HalfDay',   color: '#ea580c', bg: '#ffedd5' },
  { value: 'Leave',     color: '#2563eb', bg: '#dbeafe' },
  { value: 'WeeklyOff', color: '#7c3aed', bg: '#ede9fe' },
  { value: 'Holiday',   color: '#0891b2', bg: '#cffafe' },
];

// HR Attendance — daily grid (one row per active employee) with quick
// status pills, in/out time pickers, and remarks. Also exposes a
// Monthly Summary tab that rolls up the same data per employee.
export default function Attendance() {
  const token = sessionStorage.getItem('token');
  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const companyId = company?.id;

  const [tab, setTab] = useState(0);
  const [departments, setDepartments] = useState([]);

  // ── Daily tab ───────────────────────────────────────────────────
  // Local date (not UTC) — toISOString() gave yesterday before 05:30 IST.
  const [date, setDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({}); // employeeId -> true
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('Present');

  // ── Monthly summary tab ─────────────────────────────────────────
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [year, setYear] = useState(today.getFullYear());
  const [summary, setSummary] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const toast = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  const loadDepartments = async () => {
    const res = await fetch(
      `${config.API_BASE_URL}/Hr/Departments/${companyId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (res.ok) setDepartments(await res.json());
  };

  const loadDaily = async () => {
    if (!companyId || !date) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.append('date', date);
      if (departmentFilter) qs.append('departmentId', departmentFilter);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Attendance/Daily/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRows(
        data.map((r) => ({
          ...r,
          checkInStr: r.checkInTime ? r.checkInTime.slice(11, 16) : '',
          checkOutStr: r.checkOutTime ? r.checkOutTime.slice(11, 16) : '',
        })),
      );
      setDirty({});
    } catch (e) {
      toast('Failed to load attendance', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    if (!companyId) return;
    setSummaryLoading(true);
    try {
      const qs = new URLSearchParams();
      qs.append('month', month);
      qs.append('year', year);
      if (departmentFilter) qs.append('departmentId', departmentFilter);
      const res = await fetch(
        `${config.API_BASE_URL}/Hr/Attendance/MonthlySummary/${companyId}?${qs.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error();
      setSummary(await res.json());
    } catch (e) {
      toast('Failed to load summary', 'error');
    } finally {
      setSummaryLoading(false);
    }
  };

  useEffect(() => {
    if (companyId) loadDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (tab === 0) loadDaily();
    else loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, date, month, year, departmentFilter, companyId]);

  const updateRow = (empId, patch) => {
    setRows((prev) =>
      prev.map((r) => (r.employeeId === empId ? { ...r, ...patch } : r)),
    );
    setDirty((d) => ({ ...d, [empId]: true }));
  };

  const setStatus = (r, status) => updateRow(r.employeeId, { status });

  const saveAll = async () => {
    // Include rows edited only on time/remarks (not just status) so those edits
    // aren't silently dropped while the button claimed they'd save.
    const dirtyRows = rows.filter(
      (r) =>
        dirty[r.employeeId] &&
        (r.status || r.checkInStr || r.checkOutStr || (r.remarks && r.remarks.trim())),
    );
    if (dirtyRows.length === 0) {
      toast('No changes to save', 'warning');
      return;
    }
    setSaving(true);
    try {
      const body = {
        CompanyId: companyId,
        AttendanceDate: date,
        UserId: user?.id || null,
        Rows: dirtyRows.map((r) => ({
          Id: r.id || null,
          CompanyId: companyId,
          EmployeeId: r.employeeId,
          AttendanceDate: date,
          Status: r.status,
          CheckInTime: r.checkInStr || null,
          CheckOutTime: r.checkOutStr || null,
          Remarks: r.remarks || null,
          UserId: user?.id || null,
        })),
      };
      const res = await fetch(`${config.API_BASE_URL}/Hr/Attendance/Bulk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast('Save failed', 'error');
        return;
      }
      toast(`Saved ${data.saved} row(s)`);
      loadDaily();
    } catch (e) {
      toast('Network error', 'error');
    } finally {
      setSaving(false);
    }
  };

  const quickAction = async (employeeId, kind /* 'in' | 'out' */) => {
    try {
      const url = `${config.API_BASE_URL}/Hr/Attendance/${kind === 'in' ? 'CheckIn' : 'CheckOut'}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          CompanyId: companyId,
          EmployeeId: employeeId,
          UserId: user?.id || null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        toast(data.message || 'Action failed', 'error');
        return;
      }
      toast(kind === 'in' ? 'Checked in' : 'Checked out');
      loadDaily();
    } catch (e) {
      toast('Network error', 'error');
    }
  };

  const summaryStats = useMemo(() => {
    if (!summary.length) return null;
    return {
      total: summary.length,
      present: summary.reduce((a, b) => a + b.presentDays, 0),
      absent: summary.reduce((a, b) => a + b.absentDays, 0),
      leave: summary.reduce((a, b) => a + b.leaveDays, 0),
    };
  }, [summary]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#F1F5F9', minHeight: '100%' }}>
      <Stack
        direction='row'
        spacing={1.5}
        alignItems='center'
        sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: '#f59e0b',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <EventAvailableIcon />
        </Box>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
            Attendance
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>
            HR · Daily entry + monthly summary
          </Typography>
        </Box>
      </Stack>

      <Card sx={{ borderRadius: 2, mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label='Daily Entry' />
          <Tab label='Monthly Summary' />
        </Tabs>
      </Card>

      {tab === 0 && (
        <>
          {/* Toolbar */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}
            alignItems={{ xs: 'stretch', md: 'center' }}>
            <TextField
              type='date'
              size='small'
              label='Date'
              InputLabelProps={{ shrink: true }}
              value={date}
              onChange={(e) => setDate(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 160 }}
            />
            <TextField
              select
              size='small'
              label='Department'
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 180 }}>
              <MenuItem value=''>All Departments</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.departmentName}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flex: 1 }} />
            {/* Apply-to-all bar — pick any status and stamp it on EVERY
                visible row at once (overwrites existing statuses). Marks
                each row dirty; still requires explicit Save. */}
            <TextField
              select
              size='small'
              label='Bulk status'
              value={bulkStatus}
              onChange={(e) => setBulkStatus(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 140 }}>
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s.value} value={s.value}>
                  {s.value}
                </MenuItem>
              ))}
            </TextField>
            <Button
              variant='outlined'
              size='small'
              disabled={saving || rows.length === 0}
              onClick={() => {
                if (rows.length === 0) return;
                if (
                  !window.confirm(
                    `Apply "${bulkStatus}" to all ${rows.length} employee(s)? This overwrites any existing status.`,
                  )
                )
                  return;
                setRows((prev) =>
                  prev.map((r) => ({ ...r, status: bulkStatus })),
                );
                setDirty((d) => {
                  const next = { ...d };
                  rows.forEach((r) => {
                    next[r.employeeId] = true;
                  });
                  return next;
                });
              }}
              sx={{ mr: 1 }}>
              Apply to All
            </Button>
            {/* Non-destructive bulk shortcut — only fills rows that don't
                already have a status (so previously marked Absent/Leave
                rows are NEVER overwritten silently). Still requires the
                explicit Save button click — no race with auto-save. */}
            <Button
              variant='outlined'
              size='small'
              disabled={
                saving ||
                rows.filter((r) => !r.status).length === 0
              }
              onClick={() => {
                const unmarked = rows.filter((r) => !r.status);
                if (unmarked.length === 0) return;
                if (
                  !window.confirm(
                    `Mark ${unmarked.length} unmarked employee(s) as Present? Rows with existing status stay unchanged.`,
                  )
                )
                  return;
                setRows((prev) =>
                  prev.map((r) =>
                    r.status ? r : { ...r, status: 'Present' },
                  ),
                );
                setDirty((d) => {
                  const next = { ...d };
                  unmarked.forEach((r) => {
                    next[r.employeeId] = true;
                  });
                  return next;
                });
              }}
              sx={{ mr: 1 }}>
              Mark Unmarked Present
            </Button>
            <Button
              variant='contained'
              startIcon={<SaveIcon />}
              disabled={saving || Object.keys(dirty).length === 0}
              onClick={saveAll}
              sx={{ bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' } }}>
              {saving
                ? 'Saving…'
                : `Save ${
                    rows.filter(
                      (r) =>
                        dirty[r.employeeId] &&
                        (r.status ||
                          r.checkInStr ||
                          r.checkOutStr ||
                          (r.remarks && r.remarks.trim())),
                    ).length || ''
                  }`}
            </Button>
          </Stack>

          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Shift</TableCell>
                        <TableCell sx={{ fontWeight: 700, minWidth: 320 }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Check-In</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Check-Out</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Hours</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Late</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Remarks</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align='right'>
                          Quick
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align='center' sx={{ py: 4 }}>
                            <Typography sx={{ color: '#94a3b8' }}>
                              No active employees.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {rows.map((r) => (
                        <TableRow key={r.employeeId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {r.employeeCode}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {r.employeeName}
                          </TableCell>
                          <TableCell sx={{ color: '#475569' }}>
                            {r.departmentName || '—'}
                          </TableCell>
                          <TableCell>{r.shiftName || '—'}</TableCell>
                          <TableCell>
                            <Stack direction='row' spacing={0.5} flexWrap='wrap'>
                              {STATUS_OPTIONS.map((s) => (
                                <Chip
                                  key={s.value}
                                  size='small'
                                  label={s.value}
                                  onClick={() => setStatus(r, s.value)}
                                  sx={{
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    bgcolor:
                                      r.status === s.value ? s.color : s.bg,
                                    color: r.status === s.value ? '#fff' : s.color,
                                    border:
                                      r.status === s.value
                                        ? `1px solid ${s.color}`
                                        : '1px solid transparent',
                                  }}
                                />
                              ))}
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              type='time'
                              value={r.checkInStr}
                              onChange={(e) =>
                                updateRow(r.employeeId, {
                                  checkInStr: e.target.value,
                                })
                              }
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              type='time'
                              value={r.checkOutStr}
                              onChange={(e) =>
                                updateRow(r.employeeId, {
                                  checkOutStr: e.target.value,
                                })
                              }
                              sx={{ width: 110 }}
                            />
                          </TableCell>
                          <TableCell>{r.workingHours || 0}</TableCell>
                          <TableCell>
                            {r.lateMinutes ? `${r.lateMinutes}m` : '—'}
                          </TableCell>
                          <TableCell>
                            <TextField
                              size='small'
                              value={r.remarks || ''}
                              onChange={(e) =>
                                updateRow(r.employeeId, {
                                  remarks: e.target.value,
                                })
                              }
                              placeholder='—'
                              sx={{ minWidth: 140 }}
                            />
                          </TableCell>
                          <TableCell align='right'>
                            <Tooltip title='Check-In (now)'>
                              <IconButton
                                size='small'
                                onClick={() => quickAction(r.employeeId, 'in')}
                                sx={{ color: '#16a34a' }}>
                                <LoginIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='Check-Out (now)'>
                              <IconButton
                                size='small'
                                onClick={() => quickAction(r.employeeId, 'out')}
                                sx={{ color: '#dc2626' }}>
                                <LogoutIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {tab === 1 && (
        <>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mb: 2 }}>
            <TextField
              select
              size='small'
              label='Month'
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 140 }}>
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <MenuItem key={m} value={m}>
                  {new Date(2000, m - 1, 1).toLocaleString('en-IN', {
                    month: 'long',
                  })}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type='number'
              size='small'
              label='Year'
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              sx={{ bgcolor: '#fff', width: 110 }}
            />
            <TextField
              select
              size='small'
              label='Department'
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              sx={{ bgcolor: '#fff', minWidth: 180 }}>
              <MenuItem value=''>All</MenuItem>
              {departments.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.departmentName}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {summaryStats && (
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
              {[
                ['Total employees', summaryStats.total, '#1A56DB'],
                ['Present days', summaryStats.present, '#16a34a'],
                ['Absent days', summaryStats.absent, '#dc2626'],
                ['Leave days', summaryStats.leave, '#2563eb'],
              ].map(([label, val, color]) => (
                <Grid item xs={6} sm={3} key={label}>
                  <Card sx={{ borderLeft: `4px solid ${color}`, p: 1.5 }}>
                    <Typography sx={{ fontSize: '0.7rem', color: '#64748B' }}>
                      {label}
                    </Typography>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.2rem' }}>
                      {val}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              {summaryLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <TableContainer>
                  <Table size='small'>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#F8FAFC' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Code</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Department</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Present</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Absent</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Half</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Leave</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>WO</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Holiday</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Hours</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Late (min)</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={11} align='center' sx={{ py: 4 }}>
                            <Typography sx={{ color: '#94a3b8' }}>
                              No data for the selected period.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      {summary.map((r) => (
                        <TableRow key={r.employeeId} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {r.employeeCode}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {r.employeeName}
                          </TableCell>
                          <TableCell>{r.departmentName || '—'}</TableCell>
                          <TableCell>{r.presentDays}</TableCell>
                          <TableCell>{r.absentDays}</TableCell>
                          <TableCell>{r.halfDays}</TableCell>
                          <TableCell>{r.leaveDays}</TableCell>
                          <TableCell>{r.weeklyOffDays}</TableCell>
                          <TableCell>{r.holidayDays}</TableCell>
                          <TableCell>{r.totalWorkingHours}</TableCell>
                          <TableCell>{r.totalLateMinutes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} variant='filled'>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
